import React, { useState, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Header from './Header';
import Sidebar from './Sidebar';
import authService from '../../services/auth';

/**
 * Main Layout component for the application.
 * Includes header, the primary navigation, and content.
 *
 * Navigation adapts to the viewport:
 *   - md and up: a fixed, collapsible left rail (width persisted in localStorage).
 *   - below md:  an off-canvas drawer opened by the header's nav button, closed by
 *                tapping the backdrop, choosing a link (route change), or the
 *                sidebar's chevron. Fixes the mobile "sidebar won't open" bug.
 */
const Layout = () => {
  const isLoggedIn = authService.isLoggedIn();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem('sm-sidebar-collapsed') === '1'
  );
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const toggleSidebar = () => {
    setCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem('sm-sidebar-collapsed', next ? '1' : '0');
      return next;
    });
  };

  // Close the mobile drawer on navigation (tapping a nav link changes the route).
  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  // Lock body scroll and allow Escape to close while the drawer is open.
  useEffect(() => {
    if (!mobileNavOpen) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') setMobileNavOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [mobileNavOpen]);

  const railW = collapsed ? 'w-16' : 'w-64';
  const contentPad = collapsed ? 'md:pl-16' : 'md:pl-64';

  return (
    <div className="min-h-screen sm-app-bg text-white">
      {/* Header shown on all devices */}
      <div className="w-full">
        <Header onOpenNav={isLoggedIn ? () => setMobileNavOpen(true) : undefined} />
      </div>

      <div className="flex min-h-screen pt-16">
        {isLoggedIn && (
          <>
            {/* Desktop rail (md and up) */}
            <div className={`fixed inset-y-0 left-0 z-10 hidden ${railW} md:block transition-[width] duration-200 ease-in-out pt-16`}>
              <Sidebar collapsed={collapsed} onToggle={toggleSidebar} />
            </div>

            {/* Mobile backdrop */}
            <div
              className={`fixed inset-0 z-30 bg-black/60 md:hidden transition-opacity duration-200 ${
                mobileNavOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
              }`}
              onClick={() => setMobileNavOpen(false)}
              aria-hidden="true"
            />

            {/* Mobile off-canvas drawer (below md) */}
            <div
              className={`fixed inset-y-0 left-0 z-40 w-64 md:hidden pt-16 transform transition-transform duration-200 ease-in-out ${
                mobileNavOpen ? 'translate-x-0' : '-translate-x-full'
              }`}
              role="dialog"
              aria-modal="true"
              aria-label="Main navigation"
              aria-hidden={!mobileNavOpen}
            >
              {/* Full (expanded) sidebar; its chevron closes the drawer on mobile. */}
              <Sidebar collapsed={false} onToggle={() => setMobileNavOpen(false)} />
            </div>
          </>
        )}

        <main className={`flex-1 ${isLoggedIn ? contentPad : ''} transition-[padding] duration-200 ease-in-out`}>
          <div className="py-6">
            <div className="mx-auto px-4 sm:px-6 md:px-8">
              <Outlet />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
